import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Apollo } from "apollo-angular";
import { firstValueFrom } from "rxjs";
import { map } from "rxjs/operators";
import { RequestOptions } from "../../../config/request-options";
import { ReqIdWrapper2 } from "../../../models/request/req-id-wrapper2.model";
import { ReverseInvoiceRequest } from "../../../models/request/reverse-invoice-request.model";
import { InvoiceReversibleResponse } from "../../../models/response/invoice-reversible-response.model";
import { ReverseInvoiceResponse } from "../../../models/response/reverse-invoice-response.model";
import { INVOICE_REVERSIBLE, REVERSE_INVOICE } from "../../../gql/secure/v1/invoice";

/**
 * This file is generated from the metadata.json file. 
 * ** DO NOT edit this file manually **
 *
 * @example
 * ```
 * import { InvoiceApiService } from '@aiwyn-ai/gql-api/services/secure/v1/invoice-api.service';
 *
 * service = inject(InvoiceApiService);
 * ```
 */
@Injectable({ providedIn: 'root' })
export class InvoiceApiService {
    private readonly defaultBaseUrl: string = '/api';

    constructor(readonly apollo: Apollo, readonly http: HttpClient) {
    }

    /**
     * @param {ReqIdWrapper2} idWrapper - The idWrapper to INVOICE_REVERSIBLE.
     * @param {RequestOptions} options - Optional configuration to use REST instead of GraphQL.
     * @returns {Promise<InvoiceReversibleResponse>} A promise that resolves to the invoiceReversibleResponse.,
     *
     * @example
     * ```
     * // Using GraphQL (default)
     * const result = await this.invoiceReversible(idWrapper);
     *
     * // Using REST
     * const result = await this.invoiceReversible(idWrapper, { useRest: true });
     * ```
     */
    async invoiceReversible(idWrapper: ReqIdWrapper2, options?: RequestOptions): Promise<InvoiceReversibleResponse> {
        if (options?.useRest) {
            return this._invoiceReversibleRest(idWrapper, options.baseUrl);
        }
        return this._invoiceReversibleGraphQL(idWrapper);
    }

    private async _invoiceReversibleGraphQL(idWrapper: ReqIdWrapper2): Promise<InvoiceReversibleResponse> {
        return firstValueFrom(this.apollo.mutate({
            mutation: INVOICE_REVERSIBLE,
            variables: { idWrapper },
        }).pipe(
            map((response: any) => response.data.InvoiceReversible),
        ));
    }

    private async _invoiceReversibleRest(idWrapper: ReqIdWrapper2, baseUrl?: string): Promise<InvoiceReversibleResponse> {
        const url = `${baseUrl ?? this.defaultBaseUrl}/secure/v1/invoice/${idWrapper.id}/reversible`;
        return firstValueFrom(
            this.http.post<InvoiceReversibleResponse>(url, idWrapper)
        );
    }

    /**
     * @param {ReverseInvoiceRequest} reverseInvoiceRequest - The reverseInvoiceRequest to REVERSE_INVOICE.
     * @param {RequestOptions} options - Optional configuration to use REST instead of GraphQL.
     * @returns {Promise<ReverseInvoiceResponse>} A promise that resolves to the reverseInvoiceResponse.,
     *
     * @example
     * ```
     * // Using GraphQL (default)
     * const result = await this.reverseInvoice(reverseInvoiceRequest);
     *
     * // Using REST
     * const result = await this.reverseInvoice(reverseInvoiceRequest, { useRest: true });
     * ```
     */
    async reverseInvoice(reverseInvoiceRequest: ReverseInvoiceRequest, options?: RequestOptions): Promise<ReverseInvoiceResponse> {
        if (options?.useRest) {
            return this._reverseInvoiceRest(reverseInvoiceRequest, options.baseUrl);
        }
        return this._reverseInvoiceGraphQL(reverseInvoiceRequest);
    }

    private async _reverseInvoiceGraphQL(reverseInvoiceRequest: ReverseInvoiceRequest): Promise<ReverseInvoiceResponse> {
        return firstValueFrom(this.apollo.mutate({
            mutation: REVERSE_INVOICE,
            variables: { reverseInvoiceRequest },
        }).pipe(
            map((response: any) => response.data.ReverseInvoice),
        ));
    }

    private async _reverseInvoiceRest(reverseInvoiceRequest: ReverseInvoiceRequest, baseUrl?: string): Promise<ReverseInvoiceResponse> {
        const url = `${baseUrl ?? this.defaultBaseUrl}/secure/v1/invoice/${reverseInvoiceRequest.id}/reverse`;
        return firstValueFrom(
            this.http.post<ReverseInvoiceResponse>(url, reverseInvoiceRequest)
        );
    }
}
